"use client";

import { KeylessAccount } from "@aptos-labs/ts-sdk/keyless";
import { useMemo } from "react";

import { KEYLESS_ACCOUNTS_KEY } from "@/constants";
import { SetState, useLocalStorage } from "@/hooks/storage";
import { decodeUint8Array, PersistedUint8Array } from "@/utils/keyless/code";
import { KeylessAccountEncoding } from "@/utils/keyless/keyless";
import { EncryptedScopedIdToken } from "@/utils/keyless/types";
import { KeylessProvider } from "@/utils/keyless/window";

export interface KeylessInfo {
  email: string;
  provider: KeylessProvider;
}
interface KeylessAccountsState {
  accounts: Array<{
    idToken: { decoded: EncryptedScopedIdToken; raw: string };
    pepper: Uint8Array;
  }>;
  activeKeyless?: { account: KeylessAccount } & KeylessInfo;
}
interface KeylessAccountsActions {
  disconnectKeylessAccount: () => void;
  setPersisted: SetState<PersistedKeylessAccountsState>;
  getAccountPepperBySub: (sub: string) => PersistedUint8Array | undefined;
}

// JSON-safe persisted shape

interface PersistedEncodedKeylessAccount {
  __type: "KeylessAccount";
  data: PersistedUint8Array;
}

interface PersistedKeylessAccountsState {
  accounts: Array<{
    idToken: { decoded: EncryptedScopedIdToken; raw: string };
    pepper: PersistedUint8Array;
  }>;
  activeKeyless?: { account: PersistedEncodedKeylessAccount } & KeylessInfo;
}

const initialPersistedState: PersistedKeylessAccountsState = {
  accounts: [],
};

type ReturnType = Omit<KeylessAccountsState, "accounts"> &
  KeylessAccountsActions & { isLoading: boolean };

export function useKeylessAccounts<T = ReturnType>(selector?: (state: ReturnType) => T): T {
  const [persisted, setPersisted, isLoading] = useLocalStorage<PersistedKeylessAccountsState>(
    KEYLESS_ACCOUNTS_KEY,
    initialPersistedState,
  );

  const activeKeyless = useMemo(() => {
    if (!persisted.activeKeyless) return undefined;
    const data = decodeUint8Array(persisted.activeKeyless.account.data);
    if (!data) return undefined;
    const decoded = KeylessAccountEncoding.decode({
      __type: "KeylessAccount",
      data,
    });
    return {
      account: decoded,
      provider: persisted.activeKeyless.provider,
      email: persisted.activeKeyless.email,
    };
  }, [persisted.activeKeyless]);

  const disconnectKeylessAccount: KeylessAccountsActions["disconnectKeylessAccount"] = () =>
    setPersisted((prev) => ({ ...prev, activeKeyless: undefined }));

  const getAccountPepperBySub: KeylessAccountsActions["getAccountPepperBySub"] = (sub) => {
    const account = persisted.accounts.find((a) => a.idToken.decoded.sub === sub);
    if (!account) return undefined;
    return account.pepper;
  };

  const store: ReturnType = {
    activeKeyless,
    disconnectKeylessAccount,
    setPersisted,
    isLoading,
    getAccountPepperBySub,
  };

  return (selector ? selector(store) : (store as unknown)) as T;
}
